import { Link } from 'gatsby';
import React, { ReactElement, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { Text } from '../../../../../components';
import { FOCUS_RING_INVERSE_CONTAINER } from '../../../../button/variants';
import { trackLinks } from '../../../../../utilities';

export default function Footer(): ReactElement {
  useEffect(() => {
    trackLinks('#footer__download--figma', 'Download clicked', {
      platform: 'Figma',
    });
    trackLinks('#footer__download--figjam', 'Download clicked', {
      platform: 'Figjam',
    });
    trackLinks('#footer__download--chrome', 'Download clicked', {
      platform: 'Chrome',
    });
    trackLinks('#footer__download--firefox', 'Download clicked', {
      platform: 'Firefox',
    });
    trackLinks('#footer__download--brave', 'Download clicked', {
      platform: 'Brave',
    });
    trackLinks('#footer__download--edge', 'Download clicked', {
      platform: 'Edge',
    });
    trackLinks('#download--safari', 'Download clicked', {
      platform: 'Safari',
    });
    trackLinks('#download--arc', 'Download clicked', {
      platform: 'Arc',
    });
  }, []);

  return (
    <footer
      className={twMerge(
        'relative z-10 mt-24 flex justify-center bg-base-blue pr-6 pl-6',
        // base-blue is far too dark for the default ring — 2.14:1.
        FOCUS_RING_INVERSE_CONTAINER
      )}>
      <div className="mt-20 max-w-290 grow">
        <div className="grid grid-cols-2 gap-12 sm:grid-cols-3 md:grid-cols-4">
          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Use Cases
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/for-designers/">For Designers</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/for-developers/">For Developers</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/for-product-managers/">For Product Managers</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/for-compliance-managers/">
                  For Compliance Managers
                </Link>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Resources
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/blog/">Blog</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/library/">Library</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/release-notes/">Release Notes</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/slack/">Slack Community</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/blog/understanding-section-508-and-WCAG-compliance/">
                  WCAG Compliance
                </Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/blog/understanding-section-508-and-WCAG-compliance/">
                  Section 508 Compliance
                </Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/blog/navigating-compliance-with-the-european-accessibility-act-EAA/">
                  European Accessibility Act (EAA) Compliance
                </Link>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Help
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a href="https://app.getstark.co">My Account</a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/support/">Support Docs</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/support/contact-us/">Contact Us</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a href="/feature-request/">Feature Requests</a>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Company
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/suaf/">Stark Framework</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/privacy/">Privacy Policy</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/tos/">Terms of Service</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  className="mb-2 hover:underline has-focus-visible:underline"
                  href="https://github.com/stark-contrast/code-of-conduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="(opens in new tab)">
                  Code of Conduct
                </a>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Security
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/security/">Security Overview</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/cookies/">Cookie Policy</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/ai/">AI Overview</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <Link to="/gdpr/">GDPR</Link>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  className="mb-2"
                  href="https://status.getstark.co/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="(opens in new tab)">
                  Status
                </a>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Design Integrations
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--figma"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.figma.com/community/plugin/732603254453395948"
                  title="(opens in new tab)">
                  Stark for Figma
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--figjam"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.figma.com/community/widget/1030161589655245054"
                  title="(opens in new tab)">
                  Stark for FigJam
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--sketch"
                  href="/sketch/download/"
                  title="(opens in new tab)">
                  Stark for Sketch
                </a>
              </Text>
            </div>
          </div>

          <div className="footer__column flex flex-col items-start">
            <div className="flex flex-col">
              <Text
                role="heading"
                aria-level={6}
                level="upper"
                color="white"
                className="mb-2">
                Developer Integrations
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--chrome"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm"
                  title="(opens in new tab)">
                  Stark for Chrome
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--firefox"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://addons.mozilla.org/en-US/firefox/addon/stark-accessibility-checker/"
                  title="(opens in new tab)">
                  Stark for Firefox
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--edge"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://microsoftedge.microsoft.com/addons/detail/stark/idpodoagbkllmpdjdepbmlefgiblmnhl"
                  title="(opens in new tab)">
                  Stark for Edge
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--safari"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://apps.apple.com/us/app/stark-for-safari/id6444031666"
                  title="(opens in new tab)">
                  Stark for Safari
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--arc"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm"
                  title="(opens in new tab)">
                  Stark for Arc
                </a>
              </Text>

              <Text
                level="semibold"
                className="mb-2 hover:underline has-focus-visible:underline"
                color="bone">
                <a
                  id="footer__download--brave"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm"
                  title="(opens in new tab)">
                  Stark for Brave
                </a>
              </Text>
            </div>
          </div>

          <div></div>
        </div>

        <div className="mt-20 mb-20 flex justify-between">
          <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-8">
            <div className="footer__social grid grid-cols-5 gap-3">
              <a
                href="https://twitter.com/getstarkco"
                target="_blank"
                rel="noopener noreferrer"
                title="(opens in new tab)">
                <img
                  alt="Stark's Twitter account link"
                  src="https://res.cloudinary.com/stark-lab/image/upload/v1761249231/marketing/social-media/twitter_vhls3n.png"
                  className="w-8"
                />
              </a>
              <a
                href="https://www.instagram.com/getstarkco/"
                target="_blank"
                rel="noopener noreferrer"
                title="(opens in new tab)">
                <img
                  alt="Stark's Instagram account link"
                  src="https://res.cloudinary.com/stark-lab/image/upload/v1761249223/marketing/social-media/instagram_kv6lbz.png"
                  className="w-8"
                />
              </a>
              <a
                href="/slack/"
                target="_blank"
                rel="noopener noreferrer"
                title="(opens in new tab)">
                <img
                  alt="Stark's Slack account link"
                  src="https://res.cloudinary.com/stark-lab/image/upload/v1761249229/marketing/social-media/slack_t4zsdf.png"
                  className="w-8"
                />
              </a>
              <a
                href="https://www.linkedin.com/company/getstarkco"
                target="_blank"
                rel="noopener noreferrer"
                title="(opens in new tab)">
                <img
                  alt="Stark's LinkedIn account link"
                  src="https://res.cloudinary.com/stark-lab/image/upload/v1761249227/marketing/social-media/share-linkedin_ro0xhw.webp"
                  className="w-8"
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UCnhYIgXU1GsrLnLv-NFqVNw/featured"
                target="_blank"
                rel="noopener noreferrer"
                title="(opens in new tab)">
                <img
                  alt="Stark's YouTube account link"
                  src="https://res.cloudinary.com/stark-lab/image/upload/v1761249232/marketing/social-media/youtube_ouap2h.png"
                  className="w-8"
                />
              </a>
            </div>
            <Text level="mono" color="yellow">
              Made remotely with love by Stark Lab, Inc. Copyright{' '}
              {new Date().getFullYear()}
            </Text>
          </div>
        </div>
      </div>

      <a
        className="absolute right-4 bottom-4"
        href="https://accessibility.getstark.co"
        target="_blank"
        rel="noopener noreferrer">
        <img
          src="https://res.cloudinary.com/stark-lab/image/upload/v1761273833/accessibility-badges/monitored-by-stark-light_avj0td.png"
          height="32"
          width="147"
          alt="Monitored by Stark"
        />
      </a>
    </footer>
  );
}
